<template>
    <div class="player-control-panel">
        <div class="player-control-panel__container">
            <div v-show="!isLinesVisible" class="player-control-panel__content">
                <div class="player-control-panel__info">
                    <div class="player-control-panel__wrapper">
                        <template v-if="isEnabledProgramName">
                            <div class="player-control-panel__info-title">
                                <span v-if="isSeries">{{ episodeNumber }}. </span>{{ infoTitle }}
                            </div>
                            <div v-if="isSeries" class="player-control-panel__info-subtitle">{{ seriesName }} - Season {{ seasonNumber }}</div>
                        </template>
                        <description
                            v-if="isVisibleDescription && showInfo"
                            :text="infoDescription"
                            :info-up-btn="infoUpBtn"
                            :info-down-btn="infoDownBtn"
                            @hide-controls="hideControls"
                            @close-player="closePlayer"
                        />
                    </div>
                    <div class="player-control-panel__info-buttons">
                        <div v-if="showInfo" class="player-control-panel__button_box info-buttons__button">
                            <button
                                :id="infoBtn.id"
                                class="player-control-panel__info-button info-btn"
                                :data-type="infoBtn.dataType"
                                :data-keydown="infoBtn.dataKeydown"
                                :data-keyleft="infoBtn.dataKeyleft"
                                :data-keyright="infoBtn.dataKeyright"
                                @keydown.up="hideControls"
                                @blur="onUnfocus(infoBtn.id)"
                                @focus="onFocus(infoBtn.id)"
                                @click="onInfo"
                            >
                                <player-icon :icon-name="infoBtn.iconAttrs?.iconName" :show-descriptions="infoBtn.iconAttrs?.showDescriptions"/>
                            </button>
                            <span v-if="infoBtn.isActive">{{ labels.playerInfo }}</span>
                        </div>
                        <div class="player-control-panel__button_box info-buttons__button">
                            <loader v-if="isFavsStatusChanging || isLoadingFavoriteStatus" class="mytv-add-btn__loader loader_default-color" />
                            <button
                                :id="addBtn.id"
                                :ref="'btn_add'"
                                class="player-control-panel__info-button mytv-add-btn"
                                :data-type="addBtn.dataType"
                                :data-keydown="addBtn.dataKeydown"
                                :data-keyright="addBtn.dataKeyright"
                                :data-keyleft="addBtn.dataKeyleft"
                                @blur="onUnfocus(addBtn.id)"
                                @focus="onFocus(addBtn.id)"
                                @keydown.up="hideControls"
                                @click="onMyTvAdd"
                            >
                                <player-icon :icon-name="addBtn.iconAttrs?.iconName"  :is-favorite="addBtn.iconAttrs?.isFavorite"/>
                            </button>
                            <span v-if="addBtn.isActive">
                                {{ favoriteBtnTxt }}
                            </span>
                        </div>
                        <div v-if="props.showSubtitlesAndAudioBtn" class="player-control-panel__button_box player-control-panel__button_position-end info-buttons__button">
                            <button
                                :id="subtitlesBtn.id"
                                class="player-control-panel__info-button subtitles-btn"
                                :data-type="subtitlesBtn.dataType"
                                :data-keydown="subtitlesBtn.dataKeydown"
                                :data-keyleft="subtitlesBtn.dataKeyleft"
                                @blur="onUnfocus(subtitlesBtn.id)"
                                @focus="onFocus(subtitlesBtn.id)"
                                @keydown.up="hideControls"
                                @click="onSubtitles"
                            >
                                <player-icon :icon-name="subtitlesBtn.iconAttrs?.iconName" />
                            </button>
                            <span v-if="subtitlesBtn.isActive">{{ labels.playerSubtitles }}</span>
                        </div>
                    </div>
                </div>
                <div class="player-control-panel__track" :class="{'player-control-panel__track-indented': isLiveWithUnreachableRestart}">
                    <span
                        :id="progressBtn.id"
                        class="player-control-panel__track_line"
                        role="button"
                        tabindex="0"
                        :data-keydown="progressBtn.dataKeydown"
                        :data-keyup="progressBtn.dataKeyup"
                        @blur="onUnfocus(progressBtn.id)"
                        @focus="onFocus(progressBtn.id)"
                    />
                    <span
                        v-if="isVod"
                        id="progress-overlay"
                        ref="trackClickedElement"
                        role="button"
                        tabindex="0"
                        :data-keydown="progressBtn.dataKeydown"
                        :data-keyup="progressBtn.dataKeyup"
                        class="player-control-panel__track_padding"
                        @mouseover="onTimelineHover"
                        @click="onTimelineClick"
                    />
                    <span
                        :class="{ 'player-control-panel__track_time': true, 'focused': isProgressFocused }"
                        :style="{ left: positionTimer+'%' }"
                    >
                        {{ currentTimeFormat }}
                        <span
                            v-if="itemProcessor?.hasLive && !itemProcessor?.liveIsPausedOnce"
                            class="player-control-panel__track_time-label"
                        >
                            {{ labels.getLabelLive }}
                        </span>
                    </span>
                    <span
                        :class="{ 'player-control-panel__track_progress': true, 'focused': isProgressFocused }"
                        :style="{ width: progress+'%' }"
                    />
                    <div v-for="(item, index) in watchedRange" :key="index">
                        <span
                            :class="{ 'player-control-panel__track_progress': true, 'focused': isProgressFocused }"
                            :style="{ width: item.width +'%', left: item.start +'%' }"
                        />
                    </div>
                    <img
                        v-if="isBifVisible && !!bifImage"
                        :src="bifImage"
                        :style="{ left: positionBifImage+'%' }"
                        class="player-control-panel__track_thumbnails"
                        alt="BIF Frame"
                    >
                    <span
                        :class="{ 'player-control-panel__track_position': true, 'focused': isProgressFocused }"
                        :style="{ left: position+'%' }"
                    />
                    <span class="player-control-panel__start-time">
                        {{ startTime }}
                    </span>
                    <span class="player-control-panel__end-time">
                        {{ endTime }}
                    </span>
                </div>
                <div class="player-control-panel__buttons">
                    <div v-if="isShowBtnsNav" class="player-control-panel__button_box">
                        <button
                            :id="navBackBtn.id"
                            :class="{
                                'player-control-panel__button nav-back-btn': true, 
                                'player-control-panel__button_disable': !itemProcessor?.hasNavBack || !prevEpisode
                            }"
                            :data-keyup="navBackBtn.dataKeyup"
                            :data-keydown="navBackBtn.dataKeydown"
                            :data-keyright="navBackBtn.dataKeyright"
                            @blur="onUnfocus(navBackBtn.id)"
                            @focus="onFocus(navBackBtn.id)"
                            @click="onPrevEpisode"
                        >
                            <player-icon :icon-name="navBackBtn.iconAttrs?.iconName" :is-nav-previous="navBackBtn.iconAttrs?.isNavPrevious"/>
                        </button>
                        <span v-if="navBackBtn.isActive">{{ labels.playerSkipPrev }}</span>
                    </div>
                    <div v-if="isShowBtnsRestartAndLive" class="player-control-panel__button_box">
                        <button
                            :id="restartBtn.id"
                            :class="{
                                'player-control-panel__button restart-btn': true,
                                'player-control-panel__button_disable': isRestartBtnDisabled
                            }"
                            :data-keyup="restartBtn.dataKeyup"
                            :data-keydown="restartBtn.dataKeydown"
                            :data-keyleft="restartBtn.dataKeyleft"
                            :data-keyright="restartBtn.dataKeyright"
                            @blur="onUnfocus(restartBtn.id)"
                            @focus="onFocus(restartBtn.id)"
                            @click="onRestart"
                        >
                            <player-icon :icon-name="restartBtn.iconAttrs?.iconName" />
                        </button>
                        <span v-if="restartBtn.isActive">{{ labels.playerReset }}</span>
                    </div>
                    <div v-if="isShowBtnsSkip" class="player-control-panel__button_box">
                        <button
                            :id="skipBackBtn.id"
                            :class="{
                                'player-control-panel__button skip-back-btn': true,
                                'player-control-panel__button_disable': !rewindAvailable
                            }"
                            :data-keyup="skipBackBtn.dataKeyup"
                            :data-keydown="skipBackBtn.dataKeydown"
                            :data-keyleft="skipBackBtn.dataKeyleft"
                            :data-keyright="skipBackBtn.dataKeyright"
                            @blur="onUnfocus(skipBackBtn.id)"
                            @focus="onFocus(skipBackBtn.id)"
                            @click="onRewind"
                        >
                            <player-icon :icon-name="skipBackBtn.iconAttrs?.iconName" :is-skip-previous="skipBackBtn.iconAttrs?.isSkipPrevious"/>
                        </button>
                        <span v-if="skipBackBtn.isActive">{{ labels.playerRewind }}</span>
                    </div>
                    <div v-if="isShowBtnPlay" class="player-control-panel__button_box">
                        <button
                            :id="playBtn.id"
                            :class="{
                                'player-control-panel__button play-btn': true,
                                'player-control-panel__button_disable': !itemProcessor?.hasPause
                            }"
                            :data-keyup="playBtn.dataKeyup"
                            :data-keyleft="playBtn.dataKeyleft"
                            :data-keyright="playBtn.dataKeyright"
                            :data-keydown="playBtn.dataKeydown"
                            @blur="onUnfocus(playBtn.id)"
                            @focus="onFocus(playBtn.id)"
                            @click="togglePlay"
                        >
                            <player-icon :icon-name="playBtn.iconAttrs?.iconName" :is-play="playBtn.iconAttrs?.isPlay"/>
                        </button>
                        <span v-if="playBtn.isActive">{{ playBtnText }}</span>
                    </div>
                    <div v-if="isShowBtnsSkip" class="player-control-panel__button_box">
                        <button
                            :id="skipForwardBtn.id"
                            :class="{
                                'player-control-panel__button skip-forward-btn': true,
                                'player-control-panel__button_disable': !fastForwardAvailable
                            }"
                            :data-keyup="skipForwardBtn.dataKeyup"
                            :data-keyleft="skipForwardBtn.dataKeyleft"
                            :data-keydown="skipForwardBtn.dataKeydown"
                            :data-keyright="skipForwardBtn.dataKeyright"
                            @blur="onUnfocus(skipForwardBtn.id)"
                            @focus="onFocus(skipForwardBtn.id)"
                            @click="onFastForward"
                        >
                            <player-icon :icon-name="skipForwardBtn.iconAttrs?.iconName" :is-skip-previous="skipForwardBtn.iconAttrs?.isSkipPrevious"/>
                        </button>
                        <span v-if="skipForwardBtn.isActive">{{ labels.playerFF }}</span>
                    </div>
                    <div v-if="isShowBtnsNav" class="player-control-panel__button_box">
                        <button
                            :id="navForwardBtn.id"
                            :class="{
                                'player-control-panel__button nav-forward-btn': true,
                                'player-control-panel__button_disable': !itemProcessor?.hasNavForward || !nextEpisode
                            }"
                            :data-keyup="navForwardBtn.dataKeyup"
                            :data-keydown="navForwardBtn.dataKeydown"
                            :data-keyleft="navForwardBtn.dataKeyleft"
                            :data-keyright="navForwardBtn.dataKeyright"
                            @blur="onUnfocus(navForwardBtn.id)"
                            @focus="onFocus(navForwardBtn.id)"
                            @click="onNextEpisode"
                        >
                            <player-icon :icon-name="navForwardBtn.iconAttrs?.iconName" :is-nav-previous="navForwardBtn.iconAttrs?.isNavPrevious"/>
                        </button>
                        <span v-if="navForwardBtn.isActive">{{ labels.playerSkipNext }}</span>
                    </div>
                    <div v-if="isShowBtnsRestartAndLive" class="player-control-panel__button_box">
                        <button
                            :id="liveBtn.id"
                            :class="{
                                'player-control-panel__button live-btn': true,
                                'player-control-panel__button_disable': itemProcessor?.hasLive && !itemProcessor.liveIsPausedOnce
                            }"
                            :data-keyup="liveBtn.dataKeyup"
                            :data-keydown="liveBtn.dataKeydown"
                            :data-keyleft="liveBtn.dataKeyleft"
                            @blur="onUnfocus(liveBtn.id)"
                            @focus="onFocus(liveBtn.id)"
                            @click="onLive"
                        >
                            <player-icon :icon-name="liveBtn.iconAttrs?.iconName" />
                        </button>
                        <span v-if="liveBtn.isActive">{{ labels.playerLive }}</span>
                    </div>
                </div>
            </div>
            <div 
                v-if="isVisiblePlayerLines"
                class="player-control-panel__lines"
            >
                <PlayerLines
                    :id="AppConstants.shelfKeys.playerLinesId" 
                    ref="playerLines"
                    :is-series="isSeries"
                    :is-live="!!itemProcessor?.hasLive"
                    :item-processor="itemProcessor"
                    @list-item-selected="itemsSelected"
                    @focus="onFocusLines"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { ModelRef } from 'vue'
    import { Strings } from "@/locales/types/enums";
    import type { PlayItemRestrictionsProcessor } from "../../types/PlayItemRestrictionsProcessor";
    import { ShowTypes } from "../../types/enums/ShowTypes";
    import { type PlayItem, ProtoItem, ContentType, type RangeProgress, type OnDemandItem } from "@ca.vmedia.tv/domains";
    import { AppConstants } from "@/common";
    import type { ExposeInterface, IButtonsIds, ILabels } from "./types";
    import { storeToRefs } from 'pinia'
    import { useMedia } from "@/stores/Media";
    import { useFavoriteChannelsStore } from "@/stores/FavoriteChannels";
    import { usePlayerStore } from "@/stores/Player";
    import { useParentControlStore } from "@/stores/ParentControl";
    import type { PlayerLinesRef } from "../types";
    import type { IEventSelected } from "@/components/RowView/types";
    import { useGetButtonsData } from "@/composables/player/ControlsPane/useGetButtonsData";
    import { useGetNavigateIds } from "@/composables/player/ControlsPane/useGetNavigateIds";
    import type { AxiosError } from "axios";
    
    const MediaStoreModule = useMedia();
    const {
        getStreamFavorite,
        getStreamPropertiesLoading: isLoadingFavoriteStatus,
    } = storeToRefs(MediaStoreModule);

    const PlayerStoreModule = usePlayerStore();
    const {
        bifImage,
        isVisibleDescription,
        getIdDetailPage
    } = storeToRefs(PlayerStoreModule);
    const { saveVisibleDescription } = PlayerStoreModule;

    const { 
        deleteFromFavorites: deleteFromFavoritesMyTv, 
        addToFavorites: addToFavoritesMyTv, 
        getFavStatusChanging: isFavsStatusChanging,
        hasDeletedIdsFavorite
    } = useFavorite();

    const FavoriteChannelsStore = useFavoriteChannelsStore();
    const {
        fetchFavChannelsIds,
    } = FavoriteChannelsStore;
    const {
        getFavChannelsIds: favChannels,
    } = storeToRefs(FavoriteChannelsStore);

    const props = defineProps({
        playItem: {
			type: Object as PropType<PlayItem | null>,
        	required: true
      	},
        itemProcessor: {
			type: Object as PropType<PlayItemRestrictionsProcessor | null>,
        	required: true
      	},
        isPlaying: {
            type: Boolean,
            required: true
        },
        currentMediaTime: {
            type: Number,
            required: true
        },
        currentTimePosition: {
            type: Number,
            required: true
        },
        isModalClosed: {
            type: Boolean,
            required: true
        },
        watchedRangeAvailableFF: {
            type: Boolean,
            required: false,
            default: false
        },
        watchedRangeAvailableRW: {
            type: Boolean,
            required: false,
            default: false
        },
        prevEpisode: {
            type: Object as PropType<OnDemandItem | null>,
            required: false,
            default: null
        },
        nextEpisode: {
            type: Object as PropType<OnDemandItem | null>,
            required: false,
            default: null
        },
        isDash: {
            type: Boolean,
            required: false,
            default: false
        },
        showSubtitlesAndAudioBtn: {
            type: Boolean,
            required: true,
        },
        showControls: {
            type: Boolean,
            required: true
        },
        watchedRange: {
            type: Array as PropType<RangeProgress[]>,
            required: false,
            default: () => []
        },
        isSeeking: {
            type: Boolean,
            required: false,
            default: false
        }
    });

    const emit = defineEmits([
        'restart',
        'go-live',
        'play',
        'fastForward',
        'rewind',
        'next-episode',
        'prev-episode',
        'show-subtitles',
        'hide',
        'closePlayer',
        'list-item-selected',
        'refreshActionTime',
        'checkIsFavoriteChannel',
        'checkIsFavorite',
        'hoverOnTime',
        'seekToHoveredTime',
        'showFunctionUnavailableTooltip'
    ]);

    const { $eventBus, $navigator } = useNuxtApp();
    const { playerConfig } = useAppConfig();
    const { isEnabledProgramName, isEnableAutoCloseInfoAfterUpdateProgram } = playerConfig.controlsPanel
    const { t } = useI18n();
    const ParentControlStoreModule = useParentControlStore();
    const { getIsModalParentVisible: isTypeModeEnabledPin } = storeToRefs(ParentControlStoreModule);

    const labels: ILabels = {
        getLabelLive: t(Strings.HOME_LIVE).toString(),
        addBtnText: t(Strings.PLAYER_ADD).toString(),
        deleteBtnTxt: t(Strings.PLAYER_REMOVE).toString(),
        playerInfo: t(Strings.PLAYER_INFO).toString(),
        playerSubtitles: t(Strings.PLAYER_SUBTITLES).toString(),
        playerSkipPrev: t(Strings.PLAYER_SKIP_PREV).toString(),
        playerReset: t(Strings.PLAYER_RESET).toString(),
        playerRewind: t(Strings.PLAYER_REWIND).toString(),
        playerPause: t(Strings.PLAYER_PAUSE).toString(),
        playerPlay: t(Strings.PLAYER_PLAY).toString(),
        playerFF: t(Strings.PLAYER_FAST_FORWARD).toString(),
        playerSkipNext: t(Strings.PLAYER_SKIP_NEXT).toString(),
        playerLive: t(Strings.PLAYER_LIVE).toString()
    };
    const buttonsIds: IButtonsIds = {
        btnRestart: "btn-restart",
        btnLive: "btn-live",
        btnNavBack: "btn-nav-back",
        btnSkipBack: "btn-skip-back",
        btnPlay: "btn-play",
        btnSkipForward: "btn-skip-forward",
        btnNavForward: "btn-nav-forward",
        btnProgress: "progress",
        btnSubtitles: "btn-subtitles",
        btnAdd: "btn-add",
        btnInfo: "btn-info",
        btnInfoUp: "btn-info-up",
        btnInfoDown: "btn-info-down"
    };

    const bifOffset: number = 8;
    const bifStartPositionPercentage: number = -3;
    const endTimerPositionPercentage: number = 96;

    const isVisiblePlayerLines: Ref<boolean> = ref(false);
    const isProgressFocused: Ref<boolean> = ref(false);
    const focusedBtnId: Ref<string> = ref("");
    const isLinesVisible: Ref<boolean> = ref(false);
    const isBifEnabled: ModelRef<boolean, string> = defineModel('isBifEnabled', { type: Boolean, default: false });
    const isMarkedDeletedIds: Ref<boolean> = ref(false);
    const playerLines: Ref<PlayerLinesRef | null> = ref(null);
    const isStartedSeeking: Ref<boolean> = ref(false);
    const isRegKeyListeners: Ref<boolean> = ref(false);
    const trackClickedElement: Ref<HTMLElement | null> = ref(null);
    const hoveredTime: Ref<number> = ref(0);
    const hoveredPos: Ref<number> = ref(0);
    const isAvailableSeeking: Ref<boolean> = ref(false);

    const seeking = computed((): boolean => props.isSeeking || isStartedSeeking.value);
    const isPlaying = computed((): boolean => props.isPlaying);
    const itemProcessor = computed((): PlayItemRestrictionsProcessor | null => props.itemProcessor);
    const prevEpisode = computed((): OnDemandItem | null => props.prevEpisode);
    const nextEpisode = computed((): OnDemandItem | null => props.nextEpisode);
    
    const isShowBtnsNav = computed((): boolean => isNavigationAvailable.value && !isLiveWithUnreachableRestart.value);
    const isShowBtnsRestartAndLive = computed((): boolean => isBtnsLiveAndRestartAvailable.value && !isLiveWithUnreachableRestart.value);
    const isShowBtnsSkip = computed((): boolean => isSkipAvailable.value && !isLiveWithUnreachableRestart.value);
    const isShowBtnPlay = computed((): boolean => isLiveWithRestart.value || !itemProcessor.value?.hasLive);
    const isRestartBtnDisabled = computed((): boolean => !itemProcessor.value?.hasRestartAvailable);
    const episodeNumber = computed((): string | number => itemProcessor.value?.episodeNumber ?? "");
    const seasonNumber = computed((): string | number => itemProcessor.value?.seasonNumber ?? "");
    const seriesName = computed((): string => itemProcessor.value?.seriesName ?? "");

    const onInfo = (): void => {
        saveVisibleDescription(!isVisibleDescription.value);
    };
    const showInfo = computed((): boolean => infoDescription.value?.length > 0);
    const infoDescription = computed((): string => useGetDescription(itemProcessor.value).description.value);
    const infoTitle = computed((): string => itemProcessor.value?.title ?? "");
    const isBifVisible = computed((): boolean => !!bifImage.value && isBifEnabled.value);
    const fastForwardAvailable = computed((): boolean => props.watchedRangeAvailableFF || !!itemProcessor.value?.hasFastForward);
    const rewindAvailable = computed((): boolean => props.watchedRangeAvailableRW || !!itemProcessor.value?.hasRewind);
    const isFavorite = computed((): boolean => (itemProcessor.value?.hasLive ? favChannels.value.includes(channelId.value) : getStreamFavorite.value) && !isMarkedDeletedIds.value);
    const isLiveWithUnreachableRestart = computed((): boolean => !!itemProcessor.value?.hasLive && !itemProcessor.value?.hasRestartAvailable);
    const isLiveWithRestart = computed((): boolean => !!itemProcessor.value?.hasLive && !!itemProcessor.value.hasRestartAvailable);
    const isSkipAvailable = computed((): boolean => !itemProcessor.value?.hasLive);
    const startTime = computed((): string => itemProcessor.value?.startTimeFormatted ?? '');
    const endTime = computed((): string => itemProcessor.value?.endTimeFormatted ?? '');
    const startTimeSec = computed((): number => itemProcessor.value ? itemProcessor.value.startTimeSec : 0);
    const isBtnsLiveAndRestartAvailable = computed((): boolean => props.playItem?.showType !== ShowTypes.Series && props.playItem?.showType !== ShowTypes.Movie);
    const isNavigationAvailable = computed((): boolean => props.playItem?.showType !== ShowTypes.Movie && !itemProcessor.value?.hasLive && !!props.playItem?.showType);
    const isVod = computed((): boolean => props.itemProcessor !== null && !props.itemProcessor.hasLive && !props.itemProcessor.isVCloud);

    const currentTimeFormat = computed((): string => {
        if (itemProcessor.value == null) {
            return '';
        }
        let time = props.currentTimePosition;
        if (hoveredTime.value > 0) {
            time = hoveredTime.value;
        }
        if (itemProcessor.value.isVOD) {
            return itemProcessor.value.formatTime(time, 2);
        }

        return itemProcessor.value.formatTime(startTimeSec.value + time, 1);
    });
    const progress = computed((): number => {
        if (itemProcessor.value == null) {
            return 0;
        }
        return itemProcessor.value.getProgress(props.currentMediaTime);
    });
    const position = computed((): number => {
        if (itemProcessor.value == null) {
            return 0;
        }
        if (hoveredPos.value > 0) {
            return hoveredPos.value;
        }
        return itemProcessor.value.getProgress(props.currentTimePosition);
    });
    const positionBifImage = computed((): number => {
        if (position.value < 4) {
            return bifStartPositionPercentage;
        }
        return position.value - bifOffset;
    });
    const positionTimer = computed((): number => {
        if (itemProcessor.value == null) {
            return 0;
        }
        let itemPosition = itemProcessor.value.getProgress(props.currentTimePosition);
        if (hoveredPos.value > 0) {
            itemPosition = hoveredPos.value;
        }

        if (itemPosition >= endTimerPositionPercentage) {
            return endTimerPositionPercentage;
        }
        return itemPosition;
    });
    const channelId = computed((): number => props.playItem == null ? 0 : Number(props.playItem?.recentAirTime?.channelId));
    const itemId = computed((): number => getIdDetailPage.value ?? props.playItem?.id ?? 0);
    const itemContentType = computed((): number => (itemProcessor.value?.hasLive ? ContentType.LiveTV : props.playItem?.contentType) as number);
    const isSeries = computed((): boolean => props.playItem?.showType === ShowTypes.Series);
    const favoriteBtnTxt = computed((): string => !isFavorite.value ? labels.addBtnText : labels.deleteBtnTxt);
    const playBtnText = computed((): string => isPlaying.value ? labels.playerPause : labels.playerPlay);
        
    const focusLines = (): void => {
        if (playerLines.value) {
            playerLines.value.focusFirstRow();
            isLinesVisible.value = true;
        }
    };
    const itemsSelected = (event: IEventSelected): void => {
        emit("list-item-selected", event);
    };
    const focusDefault = (): void => {
        isLinesVisible.value = false;
        void nextTick(() => {
            let focusedElement = document.getElementById(buttonsIds.btnProgress);

            if (props.isModalClosed) {
                focusedElement = document.getElementById(buttonsIds.btnSubtitles);
            } 

            focusedElement?.focus();
            focusedElement?.scrollIntoView({block: 'end'});
        });
    };
    const isActive = (id: string): boolean => focusedBtnId.value === id;

    const {
        getBottomId,
        getTopId,
        getLeftId,
        getRightId,
    } = useGetNavigateIds({
        isRestartBtnDisabled,
        rewindAvailable,
        fastForwardAvailable,
        itemProcessor,
        prevEpisode,
        nextEpisode,
        buttonsIds,
        isShowBtnsSkip,
        isBtnsLiveAndRestartAvailable,
        isLiveWithUnreachableRestart,
        showInfo,
    });

    const {
        infoBtn,
        infoUpBtn,
        infoDownBtn,
        addBtn,
        subtitlesBtn,
        progressBtn,
        navBackBtn,
        restartBtn,
        skipBackBtn,
        playBtn,
        skipForwardBtn,
        navForwardBtn,
        liveBtn,
    } = useGetButtonsData({
        getBottomId,
        getTopId,
        getLeftId,
        getRightId,
        isActive,
        isVisibleDescription,
        isFavorite,
        isPlaying,
        buttonsIds,
    });
    const onRestart = (): void => {
        const id = getRightId(buttonsIds.btnRestart);
        if (id) {
            document.getElementById(id)?.focus();
        }
        emit("restart");
        focusDefault();
    };
    const onLive = (): void => {
        const id = getLeftId(buttonsIds.btnLive);
        if (id) {
            document.getElementById(id)?.focus();
        }
        emit("go-live", liveBtn.value.id);
    };
    const onFocusLines = (): void => {
        emit("refreshActionTime");
        focusLines();
        registerKeyListeners();
    };
    const togglePlay = (): void => {
        emit("play");
    };
    const onRewind = (): void => {
        emit("rewind");
        isBifEnabled.value = true;
    };
    const onFastForward = (): void => {
        emit("fastForward");
        isBifEnabled.value = true;
    };
    const onNextEpisode = (): void => {
        emit("next-episode");
    };
    const onPrevEpisode = (): void => {
        emit("prev-episode");
    };
    const onLeftPressed = (): void => {
        if (isProgressFocused.value) {
            onRewind();
        }
    };
    const onRightPressed = (): void => {
        if (isProgressFocused.value) {
            onFastForward();
        }
    };
    const onSubtitles = (): void => {
        emit("show-subtitles");
    };
    const seIsMarkedDeletedIds = (): void => {
        const id = channelId.value ? channelId.value : itemId.value;
        isMarkedDeletedIds.value = hasDeletedIdsFavorite(`${id}:${itemContentType.value}`);
    };
    const onMyTvAdd = async (): Promise<void> => {
        try {
            if (isFavorite.value) {
                await deleteFromFavorites();
            } else {
                await addToFavorites();
            }

            seIsMarkedDeletedIds();

            if (itemProcessor.value?.hasLive) {
                await fetchFavChannelsIds();
                emit("checkIsFavoriteChannel");
            } else {
                emit("checkIsFavorite");
            }
        } catch (error) {
            Logger.captureError(error as AxiosError);
        }
    };
    const deleteFromFavorites = async (): Promise<void> => {
        const idItem = itemProcessor.value?.hasLive ? channelId.value : itemId.value;
        await deleteFromFavoritesMyTv([new ProtoItem(idItem, itemContentType.value)]);
    };
    const addToFavorites = async (): Promise<void> => {
        const idItem = itemProcessor.value?.hasLive ? channelId.value : itemId.value;
        await addToFavoritesMyTv([new ProtoItem(idItem, itemContentType.value)]);
    };
    const hideControls = (event: KeyboardEvent | HTMLElement | null): void => {
        const element = event instanceof KeyboardEvent ? event.target as HTMLElement : event;

        if (element instanceof HTMLElement) {
            if (element.getAttribute("data-type") === "hide-button") {
                //setTimeout added so that it will work after the keyUp specified in the PlayerContainer is triggered
                setTimeout(() => {
                    emit("hide");
                }, 325);
            }
        }
    };
    const onFocus = (id: string): void => {
        emit("refreshActionTime");
        if (id === progressBtn.value.id) {
            isProgressFocused.value = true;
        } else {
            isBifEnabled.value = false;
            focusedBtnId.value = id;
        }
        registerKeyListeners();
    };
    const onUnfocus = (id: string): void => {
        if (id === progressBtn.value.id) {
            isProgressFocused.value = false;
        } else if (focusedBtnId.value === id) {
            focusedBtnId.value = "";
        }
        unregisterKeyListeners();
    };
    const closePlayer = (): void => {
        // Don't close player if PIN modal is visible
        if (isTypeModeEnabledPin.value) {
            return;
        }
        emit("closePlayer");
    };
    const onKeyDown = (event: KeyboardEvent | HTMLElement | null): void => {
        const element = event instanceof KeyboardEvent ? event.target as HTMLElement : event;

        if (element instanceof HTMLElement) {
            if(!element.dataset.keydown){
                return;
            }

            if (element.dataset.keydown == AppConstants.shelfKeys.playerLinesId) {
                focusLines();
            } else {
                document.getElementById(element.dataset.keydown)?.focus();
            }
        }
    };
    const onKeyUp = (event: KeyboardEvent | HTMLElement | null): void => {
        const element = event instanceof KeyboardEvent ? event.target as HTMLElement : event;

        if (element instanceof HTMLElement) {
            if(!element.dataset.keyup){
                hideControls(event);
            }

            if (element.dataset.keyup) {
                document.getElementById(element.dataset.keyup)?.focus();
            } else {
                focusDefault();
            }
        }
    };
    const registerKeyListeners = (): void => {
        if (!isRegKeyListeners.value) {
            isRegKeyListeners.value = true;
            $navigator.on("keypress:back", closePlayer);
            $navigator.on("keypress:down", onKeyDown);
            $navigator.on("keypress:up", onKeyUp);
        }
    };
    const unregisterKeyListeners = (): void => {
        if (isRegKeyListeners.value) {
            isRegKeyListeners.value = false;
            $navigator.off("keypress:back", closePlayer);
            $navigator.off("keypress:down", onKeyDown);
            $navigator.off("keypress:up", onKeyUp);
        }
    };
    const resetDefault = (): void => {
        if (isVisibleDescription.value && isEnableAutoCloseInfoAfterUpdateProgram) {
            saveVisibleDescription(false);
        }
    };
    const checkIsPositionInWatchedRange = (percentagePos: number): boolean => {
        if (props.watchedRange.length === 0) {
            return false;
        }
        return props.watchedRange.some((item: RangeProgress): boolean => {
            return percentagePos >= item.start && percentagePos <= item.start + item.width;
        });
    };
    const calcTimeAndPos = (event: MouseEvent): void => {
        const track = event.currentTarget as HTMLElement;
        const rect = track.getBoundingClientRect();
        const pos = event.clientX - rect.left; // Cursor position relative to the timeline
        hoveredPos.value = (pos / rect.width) * 100; // Position in percent
        hoveredTime.value = itemProcessor.value?.getTimeFromProgress(hoveredPos.value) ?? 0; // Time in position
        const skipPosition = hoveredTime.value - props.currentMediaTime; // Seek direction

        const hasFF = skipPosition > 0 && (itemProcessor.value?.hasFastForward || checkIsPositionInWatchedRange(hoveredPos.value));
        const hasRW = skipPosition < 0 && (itemProcessor.value?.hasRewind || checkIsPositionInWatchedRange(hoveredPos.value));

        if (hasRW || hasFF) {
            isBifEnabled.value = true;
            isAvailableSeeking.value = true;
        } else {
            isAvailableSeeking.value = false;
            hoveredTime.value = 0;
            hoveredPos.value = 0;
            isBifEnabled.value = false;
        }
    };
    const updateHoveredTime = (e: MouseEvent) => {
        emit("refreshActionTime");
        if (seeking.value) {
            return; // if seeking is active, do not update time
        }
        calcTimeAndPos(e)
    };

    const onMouseMove = (e: MouseEvent) => {
        updateHoveredTime(e);
    };

    const onMouseLeave = () => {
        trackClickedElement.value?.removeEventListener('mousemove', onMouseMove);
        trackClickedElement.value?.removeEventListener('mouseleave', onMouseLeave);
        hoveredPos.value = 0;
        hoveredTime.value = 0;
        isBifEnabled.value = false;
    };
    const onTimelineHover = (event: MouseEvent): void => {
        trackClickedElement.value?.addEventListener('mousemove', onMouseMove);
        trackClickedElement.value?.addEventListener('mouseleave', onMouseLeave);

        // Call the function to update hovered time
        updateHoveredTime(event);
    };
    const onTimelineClick = (event: MouseEvent) => {
        event.stopPropagation();
        onFocus(progressBtn.value.id)

        if (seeking.value) {
            return; // if seeking is active, do not update time
        }
        if (!isAvailableSeeking.value) {
            emit("showFunctionUnavailableTooltip")
            return; // if hovered time is 0, do not update time
        }

        emit('seekToHoveredTime')
        isStartedSeeking.value = true;
    };

    watch(
        fastForwardAvailable,
        (value: boolean): void => {
            if (focusedBtnId.value === buttonsIds.btnSkipForward && !value) {
                isProgressFocused.value = true;
                onFocus(buttonsIds.btnProgress);
                focusDefault();
            }
        }
    );
    watch(
        () => props.playItem,
        (newItem, oldItem): void => {
            if (newItem && oldItem && (newItem.id !== oldItem.id)) {
                resetDefault();
            }
        }
    );

    watch(
        () => props.showControls,
        (value: boolean): void => {
            if (value) {
                focusDefault();
            }
        }
    );
    watch(
        () => props.isSeeking,
        (value: boolean): void => {
            if (!value && isStartedSeeking.value) {
                isStartedSeeking.value = false;
            }
        }
    )
    watch(
        hoveredTime,
        (value: number): void => {
            emit("hoverOnTime", value);
        }
    )
    
    onMounted((): void => {
        void nextTick((): void => {
            focusDefault();
            $eventBus.on("key-up-rows", focusDefault);
            seIsMarkedDeletedIds();
            setTimeout(() => {
                isVisiblePlayerLines.value = true;
            }, 200);
        });
    });

    onBeforeUnmount((): void => {
        unregisterKeyListeners();
        $eventBus.off("key-up-rows", focusDefault);
        resetDefault();
        trackClickedElement.value?.removeEventListener('mousemove', onMouseMove);
        trackClickedElement.value?.removeEventListener('mouseleave', onMouseLeave);
        isBifEnabled.value = false;
    });

    defineExpose<ExposeInterface>({
        focusDefault,
        onLeftPressed,
        onRightPressed,
        onRewind,
        onFastForward,
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>